/**
 * ARCHIVO GENERADO — no editar a mano (ver scripts de tokens).
 *
 * Traduce cada valor de enum de GraphQL a las clases de Tailwind del token de
 * dominio correspondiente. Se escriben LITERALES a propósito: el escáner de
 * Tailwind lee el código fuente con una expresión regular y no puede resolver
 * `bg-${prefijo}-bg`, así que una plantilla dinámica generaría CSS vacío.
 */

import type {
  ContactChannel,
  CampaignStatus,
  ChurnRisk,
  CustomerTier,
  KpiStatus,
  TicketPriority,
  TicketStatus,
  TicketType,
  WarrantyStatus,
} from '@/graphql/generated';

export interface ToneClasses {
  /** Fondo del chip. */
  bg: string;
  /** Color de texto e icono. */
  fg: string;
  /** Borde de 1 px. */
  border: string;
  /** Punto sólido: leyendas de gráficos e indicadores compactos. */
  dot: string;
  /** El mismo color del borde pero como anillo, para chips con `ring-inset`. */
  ring: string;
}

/** Tono neutro de respaldo para valores que la API añada y aún no tengan token. */
export const neutralTone: ToneClasses = {
  bg: 'bg-kpi-sin-datos-bg',
  fg: 'text-kpi-sin-datos-fg',
  border: 'border-kpi-sin-datos-border',
  dot: 'bg-kpi-sin-datos-dot',
  ring: 'ring-kpi-sin-datos-border',
};

export const ticketStatusTone: Record<TicketStatus, ToneClasses> = {
  NUEVO: {
    bg: 'bg-status-nuevo-bg',
    fg: 'text-status-nuevo-fg',
    border: 'border-status-nuevo-border',
    dot: 'bg-status-nuevo-dot',
    ring: 'ring-status-nuevo-border',
  },
  EN_PROCESO: {
    bg: 'bg-status-en-proceso-bg',
    fg: 'text-status-en-proceso-fg',
    border: 'border-status-en-proceso-border',
    dot: 'bg-status-en-proceso-dot',
    ring: 'ring-status-en-proceso-border',
  },
  PENDIENTE: {
    bg: 'bg-status-pendiente-bg',
    fg: 'text-status-pendiente-fg',
    border: 'border-status-pendiente-border',
    dot: 'bg-status-pendiente-dot',
    ring: 'ring-status-pendiente-border',
  },
  ESCALADO: {
    bg: 'bg-status-escalado-bg',
    fg: 'text-status-escalado-fg',
    border: 'border-status-escalado-border',
    dot: 'bg-status-escalado-dot',
    ring: 'ring-status-escalado-border',
  },
  RESUELTO: {
    bg: 'bg-status-resuelto-bg',
    fg: 'text-status-resuelto-fg',
    border: 'border-status-resuelto-border',
    dot: 'bg-status-resuelto-dot',
    ring: 'ring-status-resuelto-border',
  },
  CERRADO: {
    bg: 'bg-status-cerrado-bg',
    fg: 'text-status-cerrado-fg',
    border: 'border-status-cerrado-border',
    dot: 'bg-status-cerrado-dot',
    ring: 'ring-status-cerrado-border',
  },
};

export const priorityTone: Record<TicketPriority, ToneClasses> = {
  ALTA: {
    bg: 'bg-priority-alta-bg',
    fg: 'text-priority-alta-fg',
    border: 'border-priority-alta-border',
    dot: 'bg-priority-alta-dot',
    ring: 'ring-priority-alta-border',
  },
  MEDIA: {
    bg: 'bg-priority-media-bg',
    fg: 'text-priority-media-fg',
    border: 'border-priority-media-border',
    dot: 'bg-priority-media-dot',
    ring: 'ring-priority-media-border',
  },
  BAJA: {
    bg: 'bg-priority-baja-bg',
    fg: 'text-priority-baja-fg',
    border: 'border-priority-baja-border',
    dot: 'bg-priority-baja-dot',
    ring: 'ring-priority-baja-border',
  },
};

export const ticketTypeTone: Record<TicketType, ToneClasses> = {
  RECLAMO: {
    bg: 'bg-type-reclamo-bg',
    fg: 'text-type-reclamo-fg',
    border: 'border-type-reclamo-border',
    dot: 'bg-type-reclamo-dot',
    ring: 'ring-type-reclamo-border',
  },
  GARANTIA: {
    bg: 'bg-type-garantia-bg',
    fg: 'text-type-garantia-fg',
    border: 'border-type-garantia-border',
    dot: 'bg-type-garantia-dot',
    ring: 'ring-type-garantia-border',
  },
  CONSULTA_TECNICA: {
    bg: 'bg-type-consulta-tecnica-bg',
    fg: 'text-type-consulta-tecnica-fg',
    border: 'border-type-consulta-tecnica-border',
    dot: 'bg-type-consulta-tecnica-dot',
    ring: 'ring-type-consulta-tecnica-border',
  },
  REPUESTO: {
    bg: 'bg-type-repuesto-bg',
    fg: 'text-type-repuesto-fg',
    border: 'border-type-repuesto-border',
    dot: 'bg-type-repuesto-dot',
    ring: 'ring-type-repuesto-border',
  },
  SEGUIMIENTO: {
    bg: 'bg-type-seguimiento-bg',
    fg: 'text-type-seguimiento-fg',
    border: 'border-type-seguimiento-border',
    dot: 'bg-type-seguimiento-dot',
    ring: 'ring-type-seguimiento-border',
  },
};

export const channelTone: Record<ContactChannel, ToneClasses> = {
  WHATSAPP: {
    bg: 'bg-channel-whatsapp-bg',
    fg: 'text-channel-whatsapp-fg',
    border: 'border-channel-whatsapp-border',
    dot: 'bg-channel-whatsapp-dot',
    ring: 'ring-channel-whatsapp-border',
  },
  FACEBOOK: {
    bg: 'bg-channel-facebook-bg',
    fg: 'text-channel-facebook-fg',
    border: 'border-channel-facebook-border',
    dot: 'bg-channel-facebook-dot',
    ring: 'ring-channel-facebook-border',
  },
  MESSENGER: {
    bg: 'bg-channel-messenger-bg',
    fg: 'text-channel-messenger-fg',
    border: 'border-channel-messenger-border',
    dot: 'bg-channel-messenger-dot',
    ring: 'ring-channel-messenger-border',
  },
  EMAIL: {
    bg: 'bg-channel-email-bg',
    fg: 'text-channel-email-fg',
    border: 'border-channel-email-border',
    dot: 'bg-channel-email-dot',
    ring: 'ring-channel-email-border',
  },
  TELEFONO: {
    bg: 'bg-channel-telefono-bg',
    fg: 'text-channel-telefono-fg',
    border: 'border-channel-telefono-border',
    dot: 'bg-channel-telefono-dot',
    ring: 'ring-channel-telefono-border',
  },
  WEB: {
    bg: 'bg-channel-web-bg',
    fg: 'text-channel-web-fg',
    border: 'border-channel-web-border',
    dot: 'bg-channel-web-dot',
    ring: 'ring-channel-web-border',
  },
  PRESENCIAL: {
    bg: 'bg-channel-presencial-bg',
    fg: 'text-channel-presencial-fg',
    border: 'border-channel-presencial-border',
    dot: 'bg-channel-presencial-dot',
    ring: 'ring-channel-presencial-border',
  },
  SISTEMA: {
    bg: 'bg-channel-sistema-bg',
    fg: 'text-channel-sistema-fg',
    border: 'border-channel-sistema-border',
    dot: 'bg-channel-sistema-dot',
    ring: 'ring-channel-sistema-border',
  },
};

export const kpiStatusTone: Record<KpiStatus, ToneClasses> = {
  CUMPLE: {
    bg: 'bg-kpi-cumple-bg',
    fg: 'text-kpi-cumple-fg',
    border: 'border-kpi-cumple-border',
    dot: 'bg-kpi-cumple-dot',
    ring: 'ring-kpi-cumple-border',
  },
  RIESGO: {
    bg: 'bg-kpi-riesgo-bg',
    fg: 'text-kpi-riesgo-fg',
    border: 'border-kpi-riesgo-border',
    dot: 'bg-kpi-riesgo-dot',
    ring: 'ring-kpi-riesgo-border',
  },
  INCUMPLE: {
    bg: 'bg-kpi-incumple-bg',
    fg: 'text-kpi-incumple-fg',
    border: 'border-kpi-incumple-border',
    dot: 'bg-kpi-incumple-dot',
    ring: 'ring-kpi-incumple-border',
  },
  SIN_DATOS: {
    bg: 'bg-kpi-sin-datos-bg',
    fg: 'text-kpi-sin-datos-fg',
    border: 'border-kpi-sin-datos-border',
    dot: 'bg-kpi-sin-datos-dot',
    ring: 'ring-kpi-sin-datos-border',
  },
};

export const customerTierTone: Record<CustomerTier, ToneClasses> = {
  BRONCE: {
    bg: 'bg-tier-bronce-bg',
    fg: 'text-tier-bronce-fg',
    border: 'border-tier-bronce-border',
    dot: 'bg-tier-bronce-dot',
    ring: 'ring-tier-bronce-border',
  },
  PLATA: {
    bg: 'bg-tier-plata-bg',
    fg: 'text-tier-plata-fg',
    border: 'border-tier-plata-border',
    dot: 'bg-tier-plata-dot',
    ring: 'ring-tier-plata-border',
  },
  ORO: {
    bg: 'bg-tier-oro-bg',
    fg: 'text-tier-oro-fg',
    border: 'border-tier-oro-border',
    dot: 'bg-tier-oro-dot',
    ring: 'ring-tier-oro-border',
  },
  PLATINO: {
    bg: 'bg-tier-platino-bg',
    fg: 'text-tier-platino-fg',
    border: 'border-tier-platino-border',
    dot: 'bg-tier-platino-dot',
    ring: 'ring-tier-platino-border',
  },
};

export const churnRiskTone: Record<ChurnRisk, ToneClasses> = {
  BAJO: {
    bg: 'bg-churn-bajo-bg',
    fg: 'text-churn-bajo-fg',
    border: 'border-churn-bajo-border',
    dot: 'bg-churn-bajo-dot',
    ring: 'ring-churn-bajo-border',
  },
  MEDIO: {
    bg: 'bg-churn-medio-bg',
    fg: 'text-churn-medio-fg',
    border: 'border-churn-medio-border',
    dot: 'bg-churn-medio-dot',
    ring: 'ring-churn-medio-border',
  },
  ALTO: {
    bg: 'bg-churn-alto-bg',
    fg: 'text-churn-alto-fg',
    border: 'border-churn-alto-border',
    dot: 'bg-churn-alto-dot',
    ring: 'ring-churn-alto-border',
  },
  CRITICO: {
    bg: 'bg-churn-critico-bg',
    fg: 'text-churn-critico-fg',
    border: 'border-churn-critico-border',
    dot: 'bg-churn-critico-dot',
    ring: 'ring-churn-critico-border',
  },
};

export const warrantyStatusTone: Record<WarrantyStatus, ToneClasses> = {
  VIGENTE: {
    bg: 'bg-warranty-vigente-bg',
    fg: 'text-warranty-vigente-fg',
    border: 'border-warranty-vigente-border',
    dot: 'bg-warranty-vigente-dot',
    ring: 'ring-warranty-vigente-border',
  },
  POR_VENCER: {
    bg: 'bg-warranty-por-vencer-bg',
    fg: 'text-warranty-por-vencer-fg',
    border: 'border-warranty-por-vencer-border',
    dot: 'bg-warranty-por-vencer-dot',
    ring: 'ring-warranty-por-vencer-border',
  },
  VENCIDA: {
    bg: 'bg-warranty-vencida-bg',
    fg: 'text-warranty-vencida-fg',
    border: 'border-warranty-vencida-border',
    dot: 'bg-warranty-vencida-dot',
    ring: 'ring-warranty-vencida-border',
  },
  ANULADA: {
    bg: 'bg-warranty-anulada-bg',
    fg: 'text-warranty-anulada-fg',
    border: 'border-warranty-anulada-border',
    dot: 'bg-warranty-anulada-dot',
    ring: 'ring-warranty-anulada-border',
  },
};

export const campaignStatusTone: Record<CampaignStatus, ToneClasses> = {
  BORRADOR: {
    bg: 'bg-campaign-borrador-bg',
    fg: 'text-campaign-borrador-fg',
    border: 'border-campaign-borrador-border',
    dot: 'bg-campaign-borrador-dot',
    ring: 'ring-campaign-borrador-border',
  },
  PROGRAMADA: {
    bg: 'bg-campaign-programada-bg',
    fg: 'text-campaign-programada-fg',
    border: 'border-campaign-programada-border',
    dot: 'bg-campaign-programada-dot',
    ring: 'ring-campaign-programada-border',
  },
  ENVIANDO: {
    bg: 'bg-campaign-enviando-bg',
    fg: 'text-campaign-enviando-fg',
    border: 'border-campaign-enviando-border',
    dot: 'bg-campaign-enviando-dot',
    ring: 'ring-campaign-enviando-border',
  },
  ENVIADA: {
    bg: 'bg-campaign-enviada-bg',
    fg: 'text-campaign-enviada-fg',
    border: 'border-campaign-enviada-border',
    dot: 'bg-campaign-enviada-dot',
    ring: 'ring-campaign-enviada-border',
  },
  FALLIDA: {
    bg: 'bg-campaign-fallida-bg',
    fg: 'text-campaign-fallida-fg',
    border: 'border-campaign-fallida-border',
    dot: 'bg-campaign-fallida-dot',
    ring: 'ring-campaign-fallida-border',
  },
  CANCELADA: {
    bg: 'bg-campaign-cancelada-bg',
    fg: 'text-campaign-cancelada-fg',
    border: 'border-campaign-cancelada-border',
    dot: 'bg-campaign-cancelada-dot',
    ring: 'ring-campaign-cancelada-border',
  },
};
